/* Durable QR requests. No API secrets; only the existing theme customer identifier and QR context. */
(function (root) {
  'use strict';
  function createQrWork(env, config) {
    // Unsupported browsers keep the existing QR flow; never run uncoordinated cart mutations.
    const enabled = config.enabled === true && typeof env.navigator.locks?.request === 'function';
    const key = 'sidiz.qr-work.v1';
    const inFlight = new Map();
    const sleep = ms => new Promise(resolve => env.setTimeout(resolve, ms));
    const read = () => { try { const value = JSON.parse(env.localStorage.getItem(key) || '[]'); return Array.isArray(value) ? value : []; } catch (_) { return []; } };
    const save = record => {
      const records = read().filter(item => item.id !== record.id && (Date.now() - item.createdAt < 86400000 || !item.finished));
      records.push(record);
      env.localStorage.setItem(key, JSON.stringify(records)); // Do not submit if the recovery record cannot be saved.
    };
    const uuid = () => env.crypto.randomUUID();
    const cookie = name => {
      const item = env.document.cookie.split('; ').find(v => v.startsWith(name + '='));
      if (!item) return null;
      const value = decodeURIComponent(item.substring(name.length + 1));
      return !value || value === 'null' || value === 'undefined' ? null : value;
    };
    const clearCookie = name => { env.document.cookie = `${name}=; path=/; Secure; SameSite=Lax; max-age=0`; };
    function remember(kind, payload, extra = {}) {
      const fingerprint = JSON.stringify([kind, payload, extra.variant || '', extra.returnPath || '']);
      const existing = read().find(r => !r.finished && r.fingerprint === fingerprint && (!r.customerGid || r.customerGid === config.customerGid));
      if (existing) return existing;
      const record = { id: uuid(), kind, payload, fingerprint, createdAt: Date.now(), customerGid: config.customerGid || null, ...extra };
      save(record);
      return record;
    }
    function storeName(code) {
      try {
        const data = JSON.parse(env.document.querySelector('.offline-shop-data')?.textContent || '{}');
        return data.offline_shops?.find(s => s.shop_code === code)?.shop_name || '';
      } catch (_) { return ''; }
    }
    function capture() {
      const url = new URL(env.location.href);
      const p = url.searchParams;
      const store = p.get('store') || cookie('store');
      const variant = p.get('variant_num') || cookie('variant_num');
      if (store && variant) {
        const record = remember('BUY', { store, store_name: storeName(store), uuid: p.get('uuid') || cookie('uuid') || 'tempuuid', series: p.get('series') || cookie('series') || 'sidiz product' }, { variant });
        if (!record.scanCounted && p.has('variant_num')) {
          record.scanCounted = true; save(record);
          env.fetch(config.base + '/api/v1/search/qr-scan-update', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ uuid: record.payload.uuid }) }).catch(() => {});
        }
      }
      const couponType = p.get('coupon_type') || cookie('coupon_type');
      if (couponType) remember('COUPON', { coupon_type: couponType, ...(store ? { store_code: store } : {}), shop_name: storeName(store) || 'UnKnown Shop Name' });
      const productStore = (url.pathname.startsWith('/products/') ? p.get('store') : null) || cookie('storeCode');
      if (productStore) remember('PRODUCT', { store_code: productStore, store_name: storeName(productStore) }, { returnPath: cookie('redirectQrProdDetail') || url.pathname });
      // All contexts have been captured independently before shared legacy cookies are cleared.
      if (read().some(r => !r.finished)) {
        ['uuid', 'store', 'variant_num', 'series', 'coupon_type', 'storeCode', 'redirectQrProdDetail'].forEach(clearCookie);
        ['uuid', 'store', 'variant_num', 'series', 'coupon_type'].forEach(name => p.delete(name));
        env.history.replaceState({}, '', url.toString());
      }
    }
    async function post(path, body) {
      const controller = new AbortController();
      const timeout = env.setTimeout(() => controller.abort(), 12000);
      try {
        const response = await env.fetch(config.base + path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: controller.signal });
        if (!response.ok) {
          const failure = new Error('요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.');
          failure.retryable = response.status >= 500 || response.status === 429;
          throw failure;
        }
        return await response.json();
      } finally { env.clearTimeout(timeout); }
    }
    async function waitForServer(record) {
      const end = Date.now() + 45000;
      while (Date.now() < end) {
        let result;
        try {
          if (!record.accepted) {
            result = await post('/api/v1/search/qr-work', { requestId: record.id, customerGid: record.customerGid, kind: record.kind, payload: record.payload });
            record.accepted = true; save(record);
          } else result = await post('/api/v1/search/qr-work/status', { requestId: record.id, customerGid: record.customerGid });
        } catch (error) {
          if (error.retryable === false) {
            record.finished = true; record.failed = true; save(record);
            error.terminal = true; throw error;
          }
          await sleep(1500); continue; // Same ID after timeout or lost acknowledgement.
        }
        if (result.status === 'COMPLETE') { record.serverDone = true; record.result = result; save(record); return result; }
        if (result.status === 'FAILED' || result.status === 'CANCELLED') {
          record.finished = true; record.failed = true; save(record);
          const failure = new Error('QR 요청을 완료하지 못했습니다. 고객센터에 확인해 주세요.');
          failure.terminal = true; throw failure;
        }
        await sleep(1500);
      }
      throw new Error('회원정보와 QR 요청을 처리하고 있습니다. 잠시 후 다시 확인해 주세요.');
    }
    async function addCartOnce(record) {
      if (!record.variant || record.cartDone) return;
      const cartResponse = await env.fetch('/cart.js');
      if (!cartResponse.ok) throw new Error('장바구니를 확인하지 못했습니다.');
      const cart = await cartResponse.json();
      const quantity = (cart.items || []).filter(item => String(item.variant_id) === String(record.variant)).reduce((sum, item) => sum + item.quantity, 0);
      if (record.cartAttempted) {
        if (quantity > record.cartBefore) { record.cartDone = true; save(record); return; }
        // An ambiguous add response must not cause a blind duplicate add on reload.
        record.finished = true; record.cartUncertain = true; save(record);
        const failure = new Error('장바구니 담기 결과를 확인하지 못했습니다. 장바구니에서 상품을 확인한 뒤 직접 결제해 주세요.');
        failure.terminal = true; throw failure;
      }
      record.cartBefore = quantity; record.cartAttempted = true; save(record);
      const response = await env.fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: record.variant, quantity: 1 }) });
      if (!response.ok) { record.cartAttempted = false; save(record); throw new Error('상품을 장바구니에 담지 못했습니다.'); }
      record.cartDone = true; save(record);
    }
    async function perform(id) {
      let record = read().find(r => r.id === id);
      if (!record || record.finished || (record.customerGid && record.customerGid !== config.customerGid)) return;
      if (!config.customerGid) return;
      record.customerGid = config.customerGid; save(record);
      const result = record.serverDone ? record.result : await waitForServer(record);
      await addCartOnce(record);
      record.finished = true; save(record);
      if (record.kind === 'BUY') {
        if (typeof env.refreshCart === 'function') await env.refreshCart();
        const sidebar = env.document.querySelector('#site-cart-sidebar');
        if (sidebar) { sidebar.classList.add('sidebar--opened'); sidebar.style.display = 'grid'; }
      } else if (record.kind === 'COUPON') {
        const message = result.processedRulesCount > 0 ? '쿠폰 발급이 완료되었습니다.' : '만료된 쿠폰입니다.';
        const shopName = record.payload.shop_name === 'UnKnown Shop Name' ? '' : (record.payload.shop_name || '');
        const safeShopName = shopName.replace(/[&<>"']/g, value => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[value]));
        if (typeof env.openMiniModalWithContent === 'function') {
          env.openMiniModalWithContent(`<div class="success-bulk-modal"><p>${safeShopName}</p><p>${message}</p><button class="body-4 bulk-success-btn">확인</button></div>`, () => {
            env.document.querySelector('.bulk-success-btn')?.addEventListener('click', () => { env.location.href = '/account?tab=coupon'; });
          });
        }
      } else if (record.returnPath?.startsWith('/products/') && env.location.pathname !== record.returnPath) env.location.href = record.returnPath;
    }
    function run(record) {
      if (inFlight.has(record.id)) return inFlight.get(record.id);
      const action = () => perform(record.id);
      // One tab owns the cart side effect; the server independently deduplicates the business work.
      // One origin-wide lock also protects the read/modify/write of the shared recovery list.
      const promise = env.navigator.locks.request('sidiz-qr-work', action).finally(() => inFlight.delete(record.id));
      inFlight.set(record.id, promise); return promise;
    }
    async function resume() {
      if (!enabled || !config.customerGid) return;
      for (const record of read().filter(r => !r.finished && (!r.customerGid || r.customerGid === config.customerGid))) await run(record);
    }
    let initialized = false;
    let ready = Promise.resolve();
    function init() {
      if (!enabled || initialized) return ready;
      initialized = true;
      ready = (async () => {
        try { await env.navigator.locks.request('sidiz-qr-work', capture); }
        catch (error) { error.terminal = true; throw error; }
        const pending = read().some(r => !r.finished && (!r.customerGid || r.customerGid === config.customerGid));
        if (!pending) return;
        if (!config.customerGid) {
          if (!/^\/(account|challenge)(\/|$)/.test(env.location.pathname) && !/\/(kakao-login|naver-login)$/.test(env.location.pathname)) env.location.href = '/account/login';
          return;
        }
        env.openLoadingModal?.();
        try { await resume(); } finally { env.closeLoadingModal?.(); }
      })();
      ready.catch(error => { env.closeLoadingModal?.(); env.alert(error.message); });
      return ready;
    }
    return { enabled, init, resume, beforeCheckout: async () => { if (!enabled) return; try { await init(); } catch (error) { if (error.terminal) throw error; } await resume(); }, _read: read, _remember: remember };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { createQrWork };
  else {
    root.SidizQrWork = createQrWork(root, root.sidizQrWorkConfig || {});
    root.document.addEventListener('DOMContentLoaded', () => { root.SidizQrWork.init(); });
    root.addEventListener('pageshow', event => { if (event.persisted && root.SidizQrWork.enabled) root.SidizQrWork.resume().catch(() => root.closeLoadingModal?.()); });
  }
})(typeof window === 'undefined' ? globalThis : window);
